import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import ClearIcon from '@mui/icons-material/Clear';
import {
  Box,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';

import { AccordionItemPanel } from 'src/components/common/Accordion';
import AccordionItem from 'src/components/common/Accordion/components/AccordionItem';
import AccordionItemHeader from 'src/components/common/Accordion/components/AccordionItemHeader';
import { CREATE_FOLLOW_UP_MODAL_ACCORDION_NAMES } from 'src/components/myFollowUp/MyFollowUpPage/CreateMyFollowUpModal/constants';
import { useCreateActionItemContext } from 'src/components/myFollowUp/MyFollowUpPage/CreateMyFollowUpModal/CreateActionItemContext';

import { useSelectSurvey } from './useSelectSurvey';

const SelectSurvey: FC = () => {
  const { t } = useTranslation('myFollowUp');
  const { surveyId } = useCreateActionItemContext();

  const {
    selectedSurveyName,
    handleSurveySelect,
    filteredSurveys,
    handleSearchChange,
    handleClearSearch,
    searchTerm,
    isLoading,
    isFetching,
    error,
  } = useSelectSurvey();

  return (
    <AccordionItem itemName={CREATE_FOLLOW_UP_MODAL_ACCORDION_NAMES.SURVEY}>
      <AccordionItemHeader
        headline={t('survey.label')}
        selectedValueToShow={selectedSurveyName}
      />
      <AccordionItemPanel disableAnimation>
        <>
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              placeholder={t('survey.searchLabel')}
              type="text"
              name="searchSurvey"
              onChange={handleSearchChange}
              value={searchTerm}
              InputProps={{
                endAdornment: searchTerm ? (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="clear search"
                      onClick={handleClearSearch}
                      edge="end"
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </Box>

          {(isLoading || isFetching) && (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {error && (
            <Typography color="error">{t('error.loadingSurveys')}</Typography>
          )}

          {!isLoading && !isFetching && filteredSurveys.length === 0 && (
            <Typography>
              {searchTerm ? t('search.noResults') : t('search.noData')}
            </Typography>
          )}

          {!isLoading && !isFetching && filteredSurveys.length > 0 && (
            <RadioGroup
              aria-label="survey selection"
              name="survey-selection"
              value={surveyId || ''}
              onChange={handleSurveySelect}
            >
              {filteredSurveys.map((survey) => (
                <FormControlLabel
                  key={survey.id}
                  value={survey.id}
                  control={<Radio />}
                  label={
                    <Typography variant="body1" component="span">
                      {survey.name}
                    </Typography>
                  }
                />
              ))}
            </RadioGroup>
          )}
        </>
      </AccordionItemPanel>
    </AccordionItem>
  );
};

export default SelectSurvey;
